import React, { memo, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ActionList,
  Avatar,
  Box,
  Button,
  Highlight,
  Icon,
  Tooltip,
} from '@node-space/ui-components'
import { numberWithCommas } from '@node-space/utils'
import { PathNames } from 'constants/General'
import { useWalletControllerContext } from 'hooks/useWalletControllerContext'
import { WalletType } from 'types/types'
import Submenu from '../Submenu'
import { formatTotal, isActiveWallet } from './utils'

interface WalletListProps {
  wallets: WalletType[]
  walletType: string
  setIsSheetOpen: React.Dispatch<SetStateAction<boolean>>
}

const WalletList: React.FC<WalletListProps> = ({ wallets, walletType, setIsSheetOpen }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { baseCurrency } = useWalletControllerContext()

  const buildWalletTableInfo = () => {
    return wallets?.map(wallet => {
      const {
        id,
        description,
        currency,
        balance,
        isEmoney,
        convertedAvailable,
        supportsInternalBvnkNetworkTransfers,
      } = wallet
      const isSelectedWalletActive = isActiveWallet(wallet)

      return {
        transparent: true,
        key: id,
        id: `wallet-row-${id}`,
        title: description,
        testid: `wallet-row-${id}`,
        ...(!isSelectedWalletActive && {
          ActionHighlight: (
            <Box paddingR={8}>
              <Highlight
                type="error"
                variant="emphasis"
                iconElement={<Icon size="small" name="Lock" />}
              >
                {t('unavailable')}
              </Highlight>
            </Box>
          ),
        }),
        ...(isEmoney && {
          HeaderHighlightLeft: (
            <Tooltip
              position="right"
              bodyContent={t('wallets.safeguardTooltip')}
              type="complex"
              actionButton={
                <Button
                  variant="ghost"
                  noPadding
                  onClick={() =>
                    window.open(
                      process.env.SAFEGUARD_LEARN_MORE_LINK,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  {t('wallets.safeguardTooltipSeeHere')}
                </Button>
              }
            >
              <div onMouseEnter={e => e.stopPropagation()} onMouseLeave={e => e.stopPropagation()}>
                <Highlight
                  type="success"
                  variant="emphasis"
                  iconElement={<Icon size="small" name="ShieldCheck" />}
                >
                  {t('wallets.safeguarded')}
                </Highlight>
              </div>
            </Tooltip>
          ),
        }),
        ...(supportsInternalBvnkNetworkTransfers && {
          HeaderHighlightRight: (
            <Highlight
              type="feature"
              variant="emphasis"
              iconElement={<Icon size="small" name="Check" />}
            >
              {t('bvnkNetwork.title')}
            </Highlight>
          ),
        }),
        Avatar: (
          <Box paddingL={8}>
            <Box className="relative">
              <Avatar name={currency?.code} />
            </Box>
          </Box>
        ),
        BodyPre: currency?.code?.toLocaleUpperCase(),
        onItemClick: (e: { stopPropagation: () => void }) => {
          e?.stopPropagation()
          navigate(`${PathNames.WALLETS}/wallet-details/${id}?tab=recent-activity`)
        },
        RightPrimaryText: `${numberWithCommas(balance)} ${currency?.code}`,
        RightSecondaryText: formatTotal(convertedAvailable, baseCurrency),
        CustomAction: (
          <Submenu wallet={wallet} setIsSheetOpen={setIsSheetOpen} type={walletType} key={id} />
        ),
      }
    })
  }

  const walletList = buildWalletTableInfo()

  return <ActionList id="wallet-action-list" items={walletList} />
}

export default memo(WalletList)
